import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_IDENTITY_API ?? '/api/v1';

export type Team = {
  id: string;
  tenant_id: string;
  organization_id?: string;
  workspace_id?: string;
  name: string;
  slug: string;
  description?: string;
  owner_id?: string;
  parent_team_id?: string;
  metadata?: Record<string, string>;
  created_at: string;
  updated_at: string;
};

export type TeamMember = {
  id: string;
  team_id: string;
  user_id: string;
  role: string;
  status: string;
  created_at: string;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function teamFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) } });
  if (!res.ok) throw new Error(`Teams ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const teamApi = {
  list: (params?: { q?: string; organization_id?: string; workspace_id?: string }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.organization_id) qs.set('organization_id', params.organization_id);
    if (params?.workspace_id) qs.set('workspace_id', params.workspace_id);
    const query = qs.toString();
    return teamFetch<Team[]>(`/teams${query ? `?${query}` : ''}`);
  },
  get: (id: string) => teamFetch<Team>(`/teams/${id}`),
  create: (input: Partial<Team> & { name: string; slug?: string }) =>
    teamFetch<Team>('/teams', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<Team>) =>
    teamFetch<Team>(`/teams/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  delete: (id: string) => teamFetch<void>(`/teams/${id}`, { method: 'DELETE' }),
  listMembers: (id: string) => teamFetch<TeamMember[]>(`/teams/${id}/members`),
  addMember: (id: string, input: { user_id: string; role?: string }) =>
    teamFetch<TeamMember>(`/teams/${id}/members`, { method: 'POST', body: JSON.stringify(input) }),
  removeMember: (id: string, userId: string) =>
    teamFetch<void>(`/teams/${id}/members/${userId}`, { method: 'DELETE' }),
  dashboard: (id: string) => teamFetch<import('./collaboration').TeamDashboard>(`/teams/${id}/dashboard`),
};
